/**
 * Mobility MVP: Complete Quiz Component
 * Orchestrates the 5-question mobility quiz flow
 */

import { useState } from 'react';
import { MobilityQuizStep, type MobilityQuestion } from './MobilityQuizStep';
import { MobilityQuizComplete } from './MobilityQuizComplete';
import { useSubmitMobilityQuiz } from '@/hooks/useMobility';
import type { MobilityQuizAnswers } from '@/lib/api/mobility';

const MOBILITY_QUESTIONS: MobilityQuestion[] = [
  {
    id: 'posterior_chain',
    title: 'Touching your toes',
    question: 'Do you have difficulty touching your toes while keeping your knees straight?',
    infoContent: `To test this, stand tall with feet hip-width apart. Bend forward slowly, keeping your knees straight.

If you can't reach mid-shin or lower without bending knees or feeling tightness in hamstrings or lower back, select Yes.`,
    type: 'yes_no_not_sure'
  },
  {
    id: 'thoracic_rotation',
    title: 'Thoracic rotation',
    question: 'Do you have difficulty rotating your chest/upper torso without moving your hips?',
    infoContent: `Sitting in a chair, cross your arms over your chest and rotate to the left and right.

If your hips want to move with you OR rotation feels limited, stiff, or uneven, select Yes.`,
    type: 'yes_no_not_sure'
  },
  {
    id: 'hip_mobility',
    title: 'Hip mobility',
    question: 'Do your hips feel tight during squatting or bending movements?',
    infoContent: `Stand with feet shoulder-width apart.

Do a bodyweight squat.

If you feel pinching, tightness, or can't squat to a comfortable depth without heels lifting, select Yes.`,
    type: 'yes_no_not_sure'
  },
  {
    id: 'shoulder_mobility',
    title: 'Shoulder overhead mobility',
    question: 'Do you experience tightness raising your arms fully overhead?',
    infoContent: `Stand tall and raise your arms straight up.

If your ribs flare, your back arches, or it feels tight in shoulders, select Yes.`,
    type: 'yes_no_not_sure'
  },
  {
    id: 'injuries',
    title: 'Injury history',
    question: 'Do you have any past injuries that affect your movement (back, hip, knee, shoulder, neck)?',
    infoContent: `This includes anything that still feels stiff, weak, limited, or occasionally painful.`,
    type: 'multi_select',
    options: ['Back', 'Hip', 'Knee', 'Shoulder', 'Neck', 'Other', 'Prefer not to say']
  }
];

interface MobilityQuizProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export function MobilityQuiz({ onComplete, onSkip }: MobilityQuizProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [showComplete, setShowComplete] = useState(false);
  const submitQuiz = useSubmitMobilityQuiz();

  const handleAnswer = (questionId: string, answer: string | string[]) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentStep < MOBILITY_QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last question - submit quiz
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      // Convert answers to API format
      const quizAnswers: MobilityQuizAnswers = {
        posterior_chain: (answers.posterior_chain as string) || 'not_sure',
        thoracic_rotation: (answers.thoracic_rotation as string) || 'not_sure',
        hip_mobility: (answers.hip_mobility as string) || 'not_sure',
        shoulder_mobility: (answers.shoulder_mobility as string) || 'not_sure',
        injuries: Array.isArray(answers.injuries) ? answers.injuries : []
      };

      await submitQuiz.mutateAsync(quizAnswers);
      setShowComplete(true);
    } catch (error) {
      console.error('Failed to submit mobility quiz:', error);
      // Still show completion screen even if submit fails
      setShowComplete(true);
    }
  };

  const handleContinue = () => {
    onComplete();
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onComplete();
    }
  };

  if (showComplete) {
    return <MobilityQuizComplete onContinue={handleContinue} onSkip={handleSkip} />;
  }

  const currentQuestion = MOBILITY_QUESTIONS[currentStep];

  return (
    <MobilityQuizStep
      question={currentQuestion}
      stepNumber={currentStep + 1}
      totalSteps={MOBILITY_QUESTIONS.length}
      selectedAnswer={answers[currentQuestion.id]}
      onAnswer={(answer) => handleAnswer(currentQuestion.id, answer)}
      onBack={handleBack}
      onNext={handleNext}
    />
  );
}
